// The curated Model Book chart's framing and keyboard navigation, as pure
// logical-range math (docs/features/CURATED_MODEL_BOOK.md § Chart framing and
// keys). A logical index is a bar's position in the series data: bar i sits
// at logical i, and a visible range {from, to} spans (to - from) bars.
//
// Every range here is meant for timeScale().setVisibleLogicalRange, which
// takes fractional logicals. (logicalToCoordinate does NOT — it returns 0 for
// a fractional input in lightweight-charts v5 — so nothing here feeds it.)

// Below this many pixels per bar the candles stop being readable, so a window
// that would need less opens on its first mark instead of fitting.
export const MIN_READABLE_SPACING = 4
// The bar spacing a long window opens at.
export const PREFERRED_SPACING = 6
// Bars kept left of the first mark when a long window opens on it. The same
// number as ModelBookEntry::WINDOW_LEAD_BARS, which already starts the window
// that far before the first mark — so with every author shown the opening
// frame starts at bar 0; this only matters on a refit with authors hidden.
export const LEAD_BARS = 60
// Space with no remembered step looks for a mark this fraction of the screen
// in from the edge, so a mark hard against it isn't taken as "next".
export const STEP_MARGIN_FRACTION = 0.05
// Zoom step: one press shows ~25% fewer (or more) bars.
export const ZOOM_STEP = 0.75
// The tightest zoom, in bars on screen.
export const MIN_VISIBLE_BARS = 10

const finite = value => typeof value === "number" && Number.isFinite(value)

// The index of the first bar on or after an ISO date, or -1. `times` is the
// series' ISO date strings in ascending order.
export function indexOnOrAfter(times, date) {
  if (!Array.isArray(times) || !date) return -1
  let lo = 0
  let hi = times.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (String(times[mid]) < date) lo = mid + 1
    else hi = mid
  }
  return lo < times.length ? lo : -1
}

// A mark's start: the earliest of its point dates (ISO strings compare as dates).
export function markStartDate(mark) {
  const dates = (mark?.points || []).map(point => point?.date).filter(Boolean).map(String)
  if (dates.length === 0) return null
  return dates.reduce((min, date) => (date < min ? date : min))
}

// The sorted, distinct bar indices at which the marks start.
export function markStartIndices(marks, times) {
  if (!Array.isArray(marks)) return []
  const indices = marks
    .map(markStartDate)
    .filter(Boolean)
    .map(date => indexOnOrAfter(times, date))
    .filter(index => index >= 0)
  return [ ...new Set(indices) ].sort((a, b) => a - b)
}

// The bar index of the earliest mark start, or null with no placeable marks.
export function firstMarkIndex(marks, times) {
  const indices = markStartIndices(marks, times)
  return indices.length > 0 ? indices[0] : null
}

// The range the chart opens on, or null for "fitContent()".
//
// A window that fits at minReadableSpacing px per bar is shown whole. A longer
// one opens at preferredSpacing with the first (visible) mark LEAD_BARS from
// the left edge — bar 0 on the opening frame, since the window starts that
// far before its first mark — never asking for from < 0 (fixLeftEdge) or past
// the last bar plus rightOffset. With no marks it opens on the latest bars.
export function initialLogicalRange({
  barCount,
  firstMarkIndex: markIndex = null,
  paneWidth,
  minReadableSpacing = MIN_READABLE_SPACING,
  preferredSpacing = PREFERRED_SPACING,
  rightOffset = 0
}) {
  if (!(barCount > 0) || !(paneWidth > 0)) return null
  if (paneWidth / barCount >= minReadableSpacing) return null

  const length = paneWidth / preferredSpacing
  const bounds = { barCount, rightOffset }
  if (!finite(markIndex)) return endRange({ from: 0, to: length }, bounds)
  const from = Math.max(0, markIndex - LEAD_BARS)
  return clampRange({ from, to: from + length }, bounds)
}

// Keep a range's length and slide it inside [0, lastBar + rightOffset].
export function clampRange(range, { barCount, rightOffset = 0 }) {
  const length = range.to - range.from
  const maxTo = barCount - 1 + rightOffset
  let from = range.from
  if (from + length > maxTo) from = maxTo - length
  if (from < 0) from = 0
  return { from, to: from + length }
}

// Slide by `fraction` of the visible length (negative = back in time).
export function scrollRange(range, fraction, bounds) {
  const shift = (range.to - range.from) * fraction
  return clampRange({ from: range.from + shift, to: range.to + shift }, bounds)
}

// The window's start at the current length.
export function homeRange(range, bounds) {
  return clampRange({ from: 0, to: range.to - range.from }, bounds)
}

// The window's end at the current length: the last bar sits rightOffset bars
// in from the right edge, as the chart's own scrollToRealTime leaves it.
export function endRange(range, { barCount, rightOffset = 0 }) {
  const to = barCount - 1 + rightOffset
  return clampRange({ from: to - (range.to - range.from), to }, { barCount, rightOffset })
}

// Zoom around the centre: factor < 1 shows fewer bars (in), > 1 more (out).
// The length stays between MIN_VISIBLE_BARS and the lesser of what
// minBarSpacing allows on the pane and the data plus its right offset.
export function zoomRange(range, factor, { paneWidth, minBarSpacing, barCount, rightOffset = 0 }) {
  const centre = (range.from + range.to) / 2
  const dataLength = barCount - 1 + rightOffset
  const spacingLength = paneWidth > 0 && minBarSpacing > 0 ? paneWidth / minBarSpacing : Infinity
  const maxLength = Math.max(MIN_VISIBLE_BARS, Math.min(spacingLength, dataLength))
  const length = Math.min(maxLength, Math.max(MIN_VISIBLE_BARS, (range.to - range.from) * factor))
  return clampRange({ from: centre - length / 2, to: centre + length / 2 }, { barCount, rightOffset })
}

// The same length, centred on `index`.
export function centreOn(range, index, bounds) {
  const half = (range.to - range.from) / 2
  return clampRange({ from: index - half, to: index + half }, bounds)
}

// The mark start Space (direction 1) or Shift+Space (-1) goes to, or null.
//
// With a remembered last step still on screen, the next mark strictly after
// it (before it, going back) — so a mark near an edge, where the centring
// clamps and the view doesn't move, is still passed. Otherwise the first mark
// at or after the screen's left edge plus a small margin (going back: the
// last at or before the right edge minus it), so the first press from the
// opening frame lands on the first mark on screen rather than skipping it.
export function stepTarget(indices, range, lastIndex, direction, marginFraction = STEP_MARGIN_FRACTION) {
  if (!Array.isArray(indices) || indices.length === 0 || !range) return null
  const remembered = finite(lastIndex) && lastIndex >= range.from && lastIndex <= range.to
  const margin = (range.to - range.from) * marginFraction
  if (direction < 0) {
    const limit = remembered ? lastIndex : range.to - margin
    for (let i = indices.length - 1; i >= 0; i--) {
      if (remembered ? indices[i] < limit : indices[i] <= limit) return indices[i]
    }
    return null
  }
  const limit = remembered ? lastIndex : range.from + margin
  return indices.find(index => (remembered ? index > limit : index >= limit)) ?? null
}
